<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import PlanungsgeschaeftGrid from '@/components/PlanungsgeschaeftGrid.vue';
import { useBrowserSessionValue } from '@kp/geofy-vuejs-lib/tools/browser_session.js';
import { instance as api } from '@/helpers/api';
import { useAuthUser } from '@/helpers/globals';
import { useBrowserStorage } from '@kp/geofy-vuejs-lib/tools/browser_local_storage.js';
import { useRouter } from 'vue-router';
import UserLoginModalCircus from '@/components/UserLoginModalCircus.vue';

const searchQuery = useBrowserSessionValue('planungsgeschaeft_my_geschaeft_search_query', '');
const loading = ref(false);
const actCollectionsFilter = useBrowserSessionValue('planungsgeschaeft_my_geschaeft_collections_filter', [
    { id: 'type', property: 'type', value: 'pg' }
]);
const showLoginCircus = ref(false);

// geschaefteCount is loaded on component mount: it
// is used to determine which view to show:
const geschaefteCount: Ref<number> = ref(0);

// we get the server total from the PlanungsgeschaeftGrid as event when it changes:
const serverTotal: Ref<number> = ref(0);

const router = useRouter();

const user = useAuthUser();

async function onNewGeschaeftClick() {
    const { getStorageValue } = useBrowserStorage('eplan');
    const svDate = getStorageValue('ignoreLot2Warning');
    let ignore = false;
    if (svDate?.length > 0) {
        const date = new Date(svDate);
        const daysDiff = new Date().getDate() - date.getDate();
        ignore = daysDiff <= 30;
    }

    // lot2 vorhanden -> zum geschaeft
    if (user?.has_username_passkey) {
        router.push({ name: 'planungsgeschaefte-create' });
        return;
    }

    // nur lot1 aber lass mich in ruhe -> zum geschaeft
    if (user && ignore) {
        router.push({ name: 'planungsgeschaefte-create' });
        return;
    }

    // nicht angem
    if (!user || !ignore) {
        showLoginCircus.value = false;
        showLoginCircus.value = true;
        return;
    }

    /*if (!user) {
        // redirect to login if user is not yet logged in:
        const redirectUrl = appUrl('/planungsgeschaefte/new');
        emitGlobalEvent('login', redirectUrl);
        return;
    }
    router.push({ name: 'planungsgeschaefte-create' });*/
}

onMounted(async () => {
    loading.value = true;
    try {
        const res = await api().post('/Planungsgeschaeft/count');
        if (res?.data?.count > 0) {
            geschaefteCount.value = Number(res.data.count);
        }
    } finally {
        loading.value = false;
    }
});
</script>

<template>
    <div class="my-5rem">
        <div class="cta-grid">
            <div>
                <h2 class="ktg-title-02">{{ $t('meine_geschaefte') }}</h2>
                <div class="loading-container my-2rem">
                    <ktg-loader v-if="geschaefteCount === 0 && loading"></ktg-loader>
                </div>
                <!-- Empty result left: -->
                <div v-if="geschaefteCount === 0 && !loading">
                    <p class="ktg-body-lead" v-html="$t('my_geschaefte_no_geschaefte_text')"></p>
                </div>
            </div>
            <!-- Empty result right: -->
            <div v-if="geschaefteCount === 0 && !loading && user && (user.isRoleGemeinde || user.isAdmin)">
                <h4 class="ktg-title-04 ml-1rem pb-0_5rem mb-2rem">{{ $t('dienstleistungen') }}</h4>
                <h5 class="ktg-subtitle-02 ml-1rem pb-0_5rem mb-1rem">{{ $t('geschaeft') }}</h5>
                <ktg-action-list>
                    <ktg-action-item href="href" :link-text="$t('starten')" @click.prevent="onNewGeschaeftClick">
                        {{ $t('create_new_geschaeft') }}
                    </ktg-action-item>
                </ktg-action-list>
            </div>
        </div>

        <div v-if="geschaefteCount > 0">
            <div class="search-container mb-1_5rem">
                <ktg-field style="width: 40ch">
                    <ktg-label for="my-geschaefte-search-field">{{ $t('search_for') }}</ktg-label>
                    <ktg-text-input
                        class="w-full"
                        v-model.lazy="searchQuery"
                        input-id="my-geschaefte-search-field"
                        type="text"
                        :placeholder="$t('searchterm')"
                        :disabled="loading"
                        appearance="grey"
                        trailing-icon="search"
                    />
                </ktg-field>
            </div>

            <PlanungsgeschaeftGrid
                sessionKey="planungsgeschaeft_my_geschaeft"
                v-model:filters="actCollectionsFilter"
                :searchQuery="searchQuery"
                :initial-limit="20"
                @loading="loading = $event"
                @server-total="serverTotal = $event"
            />
        </div>
    </div>
    <UserLoginModalCircus :user="useAuthUser()" v-model="showLoginCircus" />
</template>

<style lang="scss" scoped>
.search-container {
    display: flex;
    justify-content: flex-end;
}
.loading-container {
    display: flex;
    justify-content: center;
}
</style>
